import React, { useCallback, useEffect, useState } from "react";
import { ZipCoverageResponse } from "../../app/models/responseModels/zipCoverageResponse";
import {
  ZIP_COVERAGE_COLUMN_NAME,
  ZIP_COVERAGE_FILTER_COLUMN_OPTIONS,
  ZIP_COVERAGE_LABEL_NAME,
} from "./ZipCoverageConstants";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ExpandMore } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";

const COLUMN_WITH_DATE = ["createdOn", "updatedOn"];

type ZipCodeDataFilterProps = {
  zipCoverageData: ZipCoverageResponse[];
  setFilteredData: React.Dispatch<React.SetStateAction<ZipCoverageResponse[]>>;
  filteredData: ZipCoverageResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const ZipCodeDataFilter = ({
  zipCoverageData,
  setFilteredData,
  filteredData,
}: ZipCodeDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = React.useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [parcelCenterNameOptions, setParcelCenterNameOptions] = useState<
    string[]
  >([]); //Dropdown Options
  const [parcelCenterNameSelectedOptions, setParcelCenterNameSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [zipCodeOptions, setZipCodeOptions] = useState<string[]>([]); //Dropdown Options
  const [zipCodeSelectedOptions, setZipCodeSelectedOptions] = useState<
    string[]
  >([]); //Dropdown selected value

  const [createdOnFromDate, setCreatedOnFromDate] = useState<Date | null>(null);
  const [createdOnToDate, setCreatedOnToDate] = useState<Date | null>(null);

  const [updatedOnFromDate, setUpdatedOnFromDate] = useState<Date | null>(null);
  const [updatedOnToDate, setUpdatedOnToDate] = useState<Date | null>(null);

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
        return parcelCenterNameOptions;
      }
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
        return zipCodeOptions;
      }
      return [];
    },
    [parcelCenterNameOptions, zipCodeOptions]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
        return parcelCenterNameSelectedOptions;
      }
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
        return zipCodeSelectedOptions;
      }
      return [];
    },
    [parcelCenterNameSelectedOptions, zipCodeSelectedOptions]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
        return createdOnFromDate;
      }
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
        return updatedOnFromDate;
      }
      return null;
    },
    [createdOnFromDate, updatedOnFromDate]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
        return createdOnToDate;
      }
      if (columnName === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
        return updatedOnToDate;
      }
      return null;
    },
    [createdOnToDate, updatedOnToDate]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //Name dropdown options
    if (selectedColumnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
      setParcelCenterNameOptions(
        zipCoverageData
          .map((v) => v.parcelCenterName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setParcelCenterNameOptions(
        filteredData
          .map((v) => v.parcelCenterName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
    //Description dropdown options
    if (selectedColumnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
      setZipCodeOptions(
        zipCoverageData
          .map((v) => v.zipCode)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setZipCodeOptions(
        filteredData
          .map((v) => v.zipCode)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, zipCoverageData]);

  useEffect(() => {
    let newData = [...zipCoverageData];
    //Parcel Center Name Filter
    if (parcelCenterNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return parcelCenterNameSelectedOptions.some((s) => {
          return s === a.parcelCenterName;
        });
      });
    }

    //Zip Code Filter
    if (zipCodeSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return zipCodeSelectedOptions.some((s) => {
          return s === a.zipCode;
        });
      });
    }

    if (createdOnFromDate && createdOnToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.createdOn).toLocaleDateString() >=
            createdOnFromDate.toLocaleDateString() &&
          new Date(x.createdOn).toLocaleDateString() <=
            createdOnToDate.toLocaleDateString()
      );
    }

    if (updatedOnFromDate && updatedOnToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.updatedOn).toLocaleDateString() >=
            updatedOnFromDate.toLocaleDateString() &&
          new Date(x.updatedOn).toLocaleDateString() <=
            updatedOnToDate.toLocaleDateString()
      );
    }

    setFilteredData(newData);
  }, [
    zipCoverageData,
    setFilteredData,
    parcelCenterNameSelectedOptions,
    zipCodeSelectedOptions,
    createdOnFromDate,
    createdOnToDate,
    updatedOnFromDate,
    updatedOnToDate,
  ]);

  // ***** Handlers *****

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
      return setParcelCenterNameSelectedOptions([]);
    }
    if (removedColumnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
      return setZipCodeSelectedOptions([]);
    }
    if (removedColumnName === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
      setCreatedOnFromDate(null);
      setCreatedOnToDate(null);
      return;
    }
    if (removedColumnName === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
      setUpdatedOnFromDate(null);
      setUpdatedOnToDate(null);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
        setParcelCenterNameSelectedOptions([]);
      }
      if (oldColumnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
        setZipCodeSelectedOptions([]);
      }
    }
    if (value === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
      newInputList[index].displayColumnName =
        ZIP_COVERAGE_LABEL_NAME.PARCEL_CENTER_NAME;
    }
    if (value === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
      newInputList[index].displayColumnName = ZIP_COVERAGE_LABEL_NAME.ZIP_CODE;
    }
    if (value === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
      newInputList[index].displayColumnName = "Created On";
    }
    if (value === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
      newInputList[index].displayColumnName = "Updated On";
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setParcelCenterNameSelectedOptions([]);
    setZipCodeSelectedOptions([]);
    setCreatedOnFromDate(null);
    setCreatedOnToDate(null);
    setUpdatedOnFromDate(null);
    setUpdatedOnToDate(null);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME) {
      return setParcelCenterNameSelectedOptions(value);
    }
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE) {
      return setZipCodeSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
      return setCreatedOnFromDate(value);
    }
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.CREATED_ON) {
      return setCreatedOnToDate(value);
    }
    if (columnName === ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON) {
      return setUpdatedOnToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Zip Coverage</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {ZIP_COVERAGE_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <AutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ZipCodeDataFilter;
